import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { userSlice } from "../redux/user/userSlice";
import { getGamesData } from "../redux/asyncThunk/userActions";
import {
  CONTENT_TYPES,
  resolveGameContentType,
} from "../utils/contentType";
import { getRootTopics, normalizeKbData } from "../utils/kbTopics";

const { selectProject, showDialog, resetSelectedProject, closeDialog } =
  userSlice;

const SIDEBAR_SECTIONS = [
  {
    type: CONTENT_TYPES.knowledge_base,
    label: "База знаний",
    icon: <MenuBookOutlinedIcon fontSize="small" />,
    empty: "База знаний ещё не создана",
    emptyTopics: "Добавьте тему через «Создать проект»",
  },
  {
    type: CONTENT_TYPES.scenario,
    label: "Сценарии",
    icon: <AccountTreeOutlinedIcon fontSize="small" />,
    empty: "Пока нет сценариев",
  },
  {
    type: CONTENT_TYPES.trainer,
    label: "Тренажёр",
    icon: <QuizOutlinedIcon fontSize="small" />,
    empty: "Пока нет тренажёров",
  },
];

export default function SideMenu() {
  const dispatch = useDispatch();
  const location = useLocation();
  const games = useSelector((state) => state.user?.games);
  const liveGame = useSelector((state) => state.user?.game);
  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const user = useSelector((state) => state.user?.data?.user);
  const [expandedTypes, setExpandedTypes] = useState({});

  const activeTopicId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("topic");
  }, [location.search]);

  useEffect(() => {
    if (user?.id) {
      dispatch(getGamesData({ id: user.id }));
    }
  }, [dispatch, user?.id]);

  const groupedProjects = useMemo(() => {
    const groups = {
      [CONTENT_TYPES.knowledge_base]: [],
      [CONTENT_TYPES.scenario]: [],
      [CONTENT_TYPES.trainer]: [],
    };

    for (const game of games ?? []) {
      const type = resolveGameContentType(game);
      groups[type]?.push(game);
    }

    return groups;
  }, [games]);

  const knowledgeBaseGame = groupedProjects[CONTENT_TYPES.knowledge_base][0] ?? null;

  const knowledgeBaseSource = useMemo(() => {
    if (!knowledgeBaseGame) return null;
    if (liveGame?._id === knowledgeBaseGame._id) {
      return { ...knowledgeBaseGame, ...liveGame };
    }
    return knowledgeBaseGame;
  }, [knowledgeBaseGame, liveGame]);

  const knowledgeBaseRootTopics = useMemo(() => {
    if (!knowledgeBaseSource) return [];
    const { topics } = normalizeKbData(knowledgeBaseSource);
    return getRootTopics(topics);
  }, [knowledgeBaseSource]);

  useEffect(() => {
    if (!selectedGameId || !games?.length) return;

    const activeGame = games.find((game) => game._id === selectedGameId);
    if (activeGame) {
      const type = resolveGameContentType(activeGame);
      setExpandedTypes((current) => ({ ...current, [type]: true }));
    }
  }, [selectedGameId, games]);

  const handleActive = (id) => {
    dispatch(selectProject(id));
  };

  const handleSectionToggle = (type) => {
    setExpandedTypes((current) => ({
      ...current,
      [type]: !current[type],
    }));
  };

  const renderKnowledgeBaseLinks = () => {
    if (!knowledgeBaseGame) {
      return <div className="app-sidebar__empty">{SIDEBAR_SECTIONS[0].empty}</div>;
    }

    if (!knowledgeBaseRootTopics.length) {
      return (
        <div className="app-sidebar__empty">{SIDEBAR_SECTIONS[0].emptyTopics}</div>
      );
    }

    return (
      <ul className="app-sidebar__list">
        {knowledgeBaseRootTopics.map((topic) => {
          const isActive =
            selectedGameId === knowledgeBaseGame._id &&
            activeTopicId === topic.id;

          return (
            <li key={topic.id}>
              <Link
                to={`/main/${knowledgeBaseGame._id}?type=knowledge_base&topic=${topic.id}`}
                state={{
                  projectId: knowledgeBaseGame._id,
                  type: "edit",
                  kbTopicId: topic.id,
                }}
                className={`app-sidebar__link${
                  isActive ? " app-sidebar__link--active" : ""
                }`}
                onClick={() => {
                  handleActive(knowledgeBaseGame._id);
                  dispatch(closeDialog());
                }}
              >
                {topic.title?.trim() || "Без названия"}
              </Link>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar__top">
        <Button
          variant="contained"
          size="small"
          className="app-btn app-btn--primary"
          onClick={() => dispatch(showDialog("new"))}
        >
          Создать проект
        </Button>

        <Link to="/main" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            size="small"
            className="app-btn app-btn--secondary"
            onClick={() => dispatch(resetSelectedProject())}
          >
            Рабочий стол
          </Button>
        </Link>

        <Link to="/main/price" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            size="small"
            className="app-btn app-btn--secondary"
            onClick={() => dispatch(resetSelectedProject())}
          >
            Тарифы
          </Button>
        </Link>

        <div className="app-sidebar__sections">
          {SIDEBAR_SECTIONS.map(({ type, label, icon, empty }) => {
            const projects = groupedProjects[type] ?? [];
            const isExpanded = Boolean(expandedTypes[type]);
            const isKnowledgeBase = type === CONTENT_TYPES.knowledge_base;
            const sectionCount = isKnowledgeBase
              ? knowledgeBaseRootTopics.length
              : projects.length;

            return (
              <div
                key={type}
                className={`app-sidebar__section${
                  isExpanded ? " app-sidebar__section--expanded" : ""
                }`}
              >
                <button
                  type="button"
                  className="app-sidebar__section-toggle"
                  onClick={() => handleSectionToggle(type)}
                  aria-expanded={isExpanded}
                >
                  <span className="app-sidebar__section-toggle-main">
                    <span className="app-sidebar__section-icon">{icon}</span>
                    <span className="app-sidebar__section-label">{label}</span>
                    <span className="app-sidebar__section-count">
                      {sectionCount}
                    </span>
                  </span>
                  <ExpandMoreIcon
                    className="app-sidebar__section-chevron"
                    fontSize="small"
                  />
                </button>

                {isExpanded ? (
                  <div className="app-sidebar__section-body">
                    {isKnowledgeBase ? (
                      renderKnowledgeBaseLinks()
                    ) : !projects.length ? (
                      <div className="app-sidebar__empty">{empty}</div>
                    ) : (
                      <ul className="app-sidebar__list">
                        {projects.map((project) => (
                          <li key={project._id}>
                            <Link
                              to={`/main/${project._id}`}
                              state={{ projectId: project._id, type: "edit" }}
                              className={`app-sidebar__link${
                                selectedGameId === project._id
                                  ? " app-sidebar__link--active"
                                  : ""
                              }`}
                              onClick={() => {
                                handleActive(project._id);
                                dispatch(closeDialog());
                              }}
                            >
                              {project.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="app-sidebar__divider" />
      </div>

      <div className="app-sidebar__footer">
        <Link
          to="/main/rules"
          onClick={() => dispatch(resetSelectedProject())}
          style={{ textDecoration: "none" }}
        >
          <Button variant="contained" size="small" className="app-btn app-btn--ghost">
            Правила
          </Button>
        </Link>
      </div>
    </aside>
  );
}
